import { useToast } from 'vue-toastification'
import type { RouteLocationNormalized, RouteLocationNormalizedLoaded, Router } from 'vue-router'

import { useBookingCommerceStore } from '@/stores/bookingCommerce'
import { useBookingSelectionsStore } from '@/stores/bookingSelections'
import { useCartStore } from '@/stores/cart'
import { useCheckoutStore } from '@/stores/checkout'
import { useContentStore } from '@/stores/content'
import { useMainStore } from '@/stores/main'

import { routeName } from '@/router/routeName'

/** 12 hours in milliseconds. The fixed TTL for catalog + content data. */
export const EXPIRATION_MS = 12 * 60 * 60 * 1000

/**
 * User-facing copy shown when stale data is detected and the flow resets.
 * TODO content: migrate to a CMS-driven shared message (see
 * `contentStore.sharedMessages`) so marketing can edit without a deploy.
 */
export const EXPIRATION_TOAST_MESSAGE = 'Your session expired. Please reselect your session type.'

/**
 * Pure check — true when `loadedAt` is set and older than `EXPIRATION_MS`.
 * Returns false for `null` (no data loaded yet) so a fresh session pre-school-pick
 * never triggers expiration.
 */
export function isDataExpired(loadedAt: number | null): boolean {
  if (loadedAt === null) return false
  return Date.now() - loadedAt > EXPIRATION_MS
}

/**
 * Routes mark themselves exempt via `meta.protectFromMidFlowReset` at the
 * route definition. The pay-flow tail (`CHECKOUT_PAY`,
 * `ORDER_COMPLETE_STRIPE`, `CONFIRMATION`) opts in so an expiry hit can't
 * destroy state the user has paid for. Same flag is honored by the
 * deploy-version refresh hook in `src/router/index.ts`.
 */
export function isExpirationExemptRoute(
  route: RouteLocationNormalized | RouteLocationNormalizedLoaded
): boolean {
  return route.meta.protectFromMidFlowReset === true
}

/**
 * Clear booking selections, cart, and the data-load timestamp.
 *
 * The timestamp is cleared up front so a failed catalog reload below
 * leaves it null — `isDataExpired(null)` returns false, preventing an
 * immediate re-trigger loop. A successful reload re-stamps it.
 */
export function clearStaleSessionState(): void {
  useMainStore().setDataLoadedAt(null)
  useBookingSelectionsStore().resetBooking()
  useCartStore().resetStore()
  const checkoutStore = useCheckoutStore()
  checkoutStore.resetStudentAssignment()
  checkoutStore.resetCheckoutAcknowledgments()
}

/**
 * Reload catalog + content if a `catalogPath` is set. `loadCatalogWithContent`
 * overwrites `bookingCommerceStore.catalogData` and `contentStore.catalogContent`
 * in place — keeping `selectedSchoolDetail` and `catalogData` non-null *throughout*
 * the reset so the SESSIONS_LISTING_PAGE `requireSchoolAndCatalog` guard doesn't
 * bounce the upcoming `router.replace` back to the landing page.
 */
export async function reloadCatalogAfterExpiration(
  toast: ReturnType<typeof useToast>
): Promise<void> {
  const mainStore = useMainStore()
  if (!mainStore.catalogPath) return
  const result = await useBookingCommerceStore().loadCatalogWithContent(mainStore.catalogPath)
  if (!result.success) {
    toast.error(result.message || 'Failed to reload session catalog.')
  }
}

/**
 * Tear down stale catalog, content, cart, and booking selection state,
 * notify the user, reload the catalog, and redirect to Sessions Listing.
 * Toast fires first so the user sees the message even if reload is slow.
 * `router.replace` force-mounts even when already on Sessions Listing —
 * a stale catalog is often paired with a stale JS bundle from a deploy.
 */
export async function expireAndReset(router: Router): Promise<void> {
  const mainStore = useMainStore()
  if (mainStore.isExpiringData) return
  const contentStore = useContentStore()
  mainStore.setIsExpiringData(true)
  mainStore.setAppProcessing(true, contentStore.processingLabels.refreshingSessionLabel)
  try {
    if (import.meta.env.DEV) {
      console.info('[dataExpiration] catalog/content TTL exceeded — resetting')
    }
    const toast = useToast()
    toast.info(EXPIRATION_TOAST_MESSAGE)
    clearStaleSessionState()
    await reloadCatalogAfterExpiration(toast)
    await router.replace({ name: routeName.SESSIONS_LISTING_PAGE })
  } finally {
    mainStore.setAppProcessing(false)
    mainStore.setIsExpiringData(false)
  }
}
